import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useCalculos } from "@/hooks/useCalculos";
import { CalculoRMCResult } from "@/types/calculos";
import { addToHistory, useCalculoPrefill } from "@/hooks/useCalculoHistory";

export const CalculosRMC = () => {
  const [salarioLiquido, setSalarioLiquido] = useState("");
  const [margem, setMargem] = useState("30");
  const [resultado, setResultado] = useState<CalculoRMCResult | null>(null);
  
  const { calcularRMC, loading } = useCalculos();

  useCalculoPrefill("rmc", (v) => {
    setSalarioLiquido(v.salarioLiquido ?? "");
    setMargem(v.margem ?? "30");
    setResultado(null);
  });

  const handleCalcular = async () => {
    if (!salarioLiquido || !margem) {
      return;
    }

    try {
      const result = await calcularRMC({
        salarioLiquido: parseFloat(salarioLiquido),
        margem: parseFloat(margem)
      });
      setResultado(result);
      addToHistory({
        type: "rmc",
        label: "RMC - Margem Consignável",
        values: { salarioLiquido, margem },
        summary: `Salário ${formatCurrency(parseFloat(salarioLiquido))} • Disponível ${formatCurrency(result.valorDisponivelConsignado)}`,
      });
    } catch (error) {
      console.error("Erro no cálculo:", error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>RMC - Reserva de Margem Consignável</CardTitle>
        <p className="text-sm text-muted-foreground">
          Cálculo da margem disponível para empréstimo consignado
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="salario-liquido">Salário Líquido</Label>
            <Input
              id="salario-liquido"
              type="number"
              placeholder="3000.00"
              value={salarioLiquido}
              onChange={(e) => setSalarioLiquido(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="margem">Margem Consignável (%)</Label>
            <Input
              id="margem"
              type="number"
              step="0.1"
              placeholder="30"
              value={margem}
              onChange={(e) => setMargem(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Máximo legal: 30% (servidores) / 35% (INSS)
            </p>
          </div>
        </div>
        
        <Button 
          onClick={handleCalcular} 
          className="w-full"
          disabled={loading}
        >
          {loading ? "Calculando..." : "Calcular RMC"}
        </Button>
        
        {resultado && (
          <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
            <h3 className="font-semibold mb-2">Margem Consignável:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Salário Base:</strong> {formatCurrency(parseFloat(salarioLiquido))}</p>
                <p><strong>Margem ({margem}%):</strong> {formatCurrency(resultado.valorDisponivelConsignado)}</p>
              </div>
              <div>
                <p className="text-lg font-bold text-green-600">
                  <strong>Valor Disponível:</strong> {formatCurrency(resultado.valorDisponivelConsignado)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Para empréstimos consignados
                </p>
              </div>
            </div>
            <div className="mt-3 p-2 bg-blue-50 rounded text-xs">
              <p><strong>RMC (5% da margem):</strong> {formatCurrency(resultado.parcelaMaxima)}</p>
              <p className="text-muted-foreground">Reserva mínima obrigatória</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};